// # UWorld Question Bank Component
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { Lock, Edit, Save, Cancel, Shuffle } from '@mui/icons-material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import './uw1.css';
import { 
  FaCalendarAlt, 
  FaClock,
  FaBook,
  FaClipboardCheck,
  FaChartLine,
  FaTasks,
  FaUserClock,
  FaListAlt,
  FaUserMd,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaRegCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

// # Import Firebase
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import cacheManager from '../../../auth/cache_manager';

// # Import step icons
import stepOneIcon from '../../../aneo_media/mentee/one.png';

// # Import role access utility
import m1Access from '../m1_access';

// # Define chart colors
const CHART_COLORS = ['#3498db', '#2ecc71', '#e74c3c', '#f1c40f', '#9b59b6'];

// # Define status options
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed'];

// # Define systems for step 1
const SYSTEMS = [
  "Allergy & Immunology",
  "Biochemistry (General Principles)",
  "Biostatistics & Epidemiology", 
  "Cardiovascular",
  "Dermatology",
  "Ear, Nose & Throat (ENT)",
  "Endocrine, Diabetes & Metabolism", 
  "Female Reproductive System & Breast",
  "Gastrointestinal & Nutrition",
  "Genetics (General Principles)", 
  "Hematology & Oncology",
  "Infectious Diseases", 
  "Male Reproductive System",
  "Microbiology (General Principles)",
  "Miscellaneous (Multisystem)",
  "Nervous System",
  "Ophthalmology",
  "Pathology (General Principles)",
  "Pharmacology (General Principles)",
  "Poisoning & Environmental Exposure",
  "Pregnancy, Childbirth & Puerperium",
  "Psychiatric/Behavioral & Substance Use Disorder",
  "Pulmonary & Critical Care",
  "Renal, Urinary Systems & Electrolytes",    
  "Rheumatology/Orthopedics & Sports",
  "Social Sciences (Ethics/Legal/Professional)",
  "Random" // # Added Random chapter
];

function UWorldQBank({ isAdmin, isTutor, isMentorView, menteeId }) {
  const navigate = useNavigate();
  const { username } = useParams(); // # Get mentee username from URL if in mentor view
  const { userProfile, user } = useAuthStore();
  const [qbankData, setQbankData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [tempData, setTempData] = useState({});
  const [selectedChart, setSelectedChart] = useState('bar');
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  // # Add dark theme state
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # Access is always granted for admin/tutor, subscription check is handled by ProtectedRouteUser
  const stepAccess = isAdmin || isTutor ? true : true;

  // # Get the correct user ID based on role context
  useEffect(() => {
    // # Use the access utility to determine the target user ID
    const userId = m1Access.getTargetUserId({
      isAdmin,
      isTutor,
      viewingMenteeId: menteeId || sessionStorage.getItem('currentViewingMenteeId'),
      currentUserId: user?.uid
    });
    
    setTargetUserId(userId);
    setIsMentor(userId !== user?.uid);
    
    // # Determine if current user can edit content
    setCanEdit(m1Access.canEditContent({
      isAdmin,
      isTutor,
      isMentorView
    }) || userId === user?.uid);
  }, [user?.uid, isAdmin, isTutor, isMentorView, menteeId]);

  // # Load data from Firestore
  useEffect(() => {
    const loadQBankData = async () => {
      if (!targetUserId) return;

      try {
        // # Only load data if we have access
        if (stepAccess) {
          const docRef = doc(db, 's1_mentee', targetUserId);
          const docSnap = await getDoc(docRef);
          const uworldData = docSnap.data()?.uworld || {};

          // # Initialize with loaded data or default values
          const initialData = SYSTEMS.reduce((acc, system) => {
            acc[system] = {
              status: uworldData[system]?.status || 'Not Started',
              percentage: uworldData[system]?.percentage || 0,
              correct: uworldData[system]?.correct || 0,
              incorrect: uworldData[system]?.incorrect || 0,
              weaknesses: uworldData[system]?.weaknesses || []
            };
            return acc;
          }, {});

          setQbankData(initialData);
        }
      } catch (error) {
        console.error('Error loading qbank data:', error);
      }
    };

    loadQBankData();
  }, [targetUserId, stepAccess]);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Calculate dashboard stats
  const calculateStats = (stepData) => {
    if (!stepData) return {
      totalCompleted: 0,
      totalChapters: 0,
      totalCorrect: 0,
      totalIncorrect: 0,
      totalWeakPoints: 0,
      totalPercentage: 0,
      averagePercentage: 0
    };

    // Filter out the Random system from calculations
    const systems = Object.entries(stepData).filter(([system]) => system !== "Random");
    const totalCompleted = systems.filter(([_, sys]) => sys?.status === 'Completed').length;
    const totalChapters = systems.length;
    const totalCorrect = systems.reduce((sum, [_, sys]) => sum + (sys?.correct || 0), 0);
    const totalIncorrect = systems.reduce((sum, [_, sys]) => sum + (sys?.incorrect || 0), 0);
    const totalWeakPoints = systems.reduce((sum, [_, sys]) => sum + ((sys?.weaknesses || []).length || 0), 0);
    const totalPercentage = totalCorrect + totalIncorrect > 0 
      ? ((totalCorrect / (totalCorrect + totalIncorrect)) * 100).toFixed(1)
      : 0;
    const averagePercentage = systems.length > 0 
      ? systems.reduce((sum, [_, sys]) => sum + (sys?.percentage || 0), 0) / systems.length 
      : 0;

    return {
      totalCompleted,
      totalChapters,
      totalCorrect,
      totalIncorrect,
      totalWeakPoints,
      totalPercentage,
      averagePercentage
    };
  };

  // # Prepare chart data
  const getChartData = (stepData) => {
    return Object.entries(stepData).map(([system, data]) => ({
      name: system.split(' ')[0], // Use first word for shorter labels
      percentage: data.percentage
    }));
  };

  // # Toggle edit mode - only allow if user can edit
  const toggleEditMode = () => {
    if (!canEdit) return;
    
    if (editMode) {
      // # Cancel changes
      setTempData({ ...qbankData });
    } else {
      // # Enter edit mode
      setTempData({ ...qbankData });
    }
    setEditMode(!editMode);
  };

  // # Handle input change
  const handleInputChange = (system, field, value) => {
    if (field === 'weaknesses') {
      setTempData(prev => ({
        ...prev,
        [system]: {
          ...prev[system],
          weaknesses: value
        }
      }));
      return;
    }

    // # For percentage field, convert to number
    const numValue = field === 'percentage' ? (value === '' ? '' : Number(value)) : value;
    
    setTempData(prev => ({
      ...prev,
      [system]: {
        ...prev[system],
        [field]: numValue
      }
    }));
  };

  // # Update saveChanges to include Firestore save
  const saveChanges = async () => {
    const calculatedData = { ...tempData };

    // # Calculate for each system
    Object.keys(calculatedData).forEach(system => {
      const data = calculatedData[system];
      
      // # Skip if no numeric values entered
      if (!data.correct && !data.incorrect && !data.percentage) {
        return;
      }

      // # Convert empty strings to 0
      const correct = data.correct === '' ? 0 : Number(data.correct || 0);
      const incorrect = data.incorrect === '' ? 0 : Number(data.incorrect || 0);
      const percentage = data.percentage === '' ? 0 : Number(data.percentage || 0);

      // # Calculate missing values
      if (correct && incorrect) {
        // # Calculate percentage from correct and incorrect
        const total = correct + incorrect;
        calculatedData[system].percentage = total > 0 ? ((correct / total) * 100).toFixed(1) : 0;
      } else if (correct && percentage) {
        // # Calculate incorrect from correct and percentage
        calculatedData[system].incorrect = Math.round((correct * 100 / percentage) - correct);
      } else if (incorrect && percentage) {
        // # Calculate correct from incorrect and percentage
        calculatedData[system].correct = Math.round((incorrect * percentage) / (100 - percentage));
      }

      // # Ensure all values are numbers, not strings
      calculatedData[system].correct = Number(calculatedData[system].correct || 0);
      calculatedData[system].incorrect = Number(calculatedData[system].incorrect || 0);
      calculatedData[system].percentage = Number(calculatedData[system].percentage || 0);
    });

    // # Update local state
    setQbankData(calculatedData);

    // # Save to Firestore
    await saveToFirestore(calculatedData);

    // # Exit edit mode
    setEditMode(false);
  };

  // # Prepare pie chart data with total correct/incorrect only
  const getPieChartData = (stepData) => {
    const totalCorrect = Object.values(stepData).reduce((sum, data) => sum + (data.correct || 0), 0);
    const totalIncorrect = Object.values(stepData).reduce((sum, data) => sum + (data.incorrect || 0), 0);
    
    return [
      {
        name: 'Correct',
        value: totalCorrect,
        color: '#2ecc71' // Green for correct
      },
      {
        name: 'Incorrect',
        value: totalIncorrect,
        color: '#e74c3c' // Red for incorrect
      }
    ].filter(item => item.value > 0); // Only show segments with values
  };

  // # Prepare line chart data
  const getLineChartData = (stepData) => {
    return Object.entries(stepData).map(([system, data]) => ({
      name: system.split(' ')[0],
      progress: data.percentage
    }));
  };

  // # Modified chart rendering with better sizing
  const renderChart = (data) => {
    const margin = { top: 20, right: 30, left: 20, bottom: 100 };
    const containerWidth = document.querySelector('.uw1_chart_container')?.clientWidth || 600;
    const chartWidth = Math.min(Math.max(getChartData(data).length * 80, containerWidth - 40), containerWidth);
    
    switch (selectedChart) {
      case 'bar':
        return (
          <div className="uw1_chart_scroll_container">
            <BarChart 
              data={getChartData(data)}
              margin={margin}
              width={chartWidth}
              height={300}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="percentage" fill="#3498db" />
            </BarChart>
          </div>
        );
      case 'pie':
        return (
          <div className="uw1_chart_scroll_container">
            <div className="uw1_pie_chart_container">
              <PieChart
                width={Math.min(800, containerWidth - 40)}
                height={600}
                margin={{ top: 40, right: 30, left: 30, bottom: 40 }}
              >
                <Pie
                  data={getPieChartData(data)}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  outerRadius={Math.min(180, (containerWidth - 120) / 4)}
                  label={({ name, value, percent }) => 
                    `${name}: ${value} (${(percent * 100).toFixed(1)}%)`
                  }
                >
                  {getPieChartData(data).map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`Total: ${value}`, name]}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  wrapperStyle={{ 
                    paddingTop: '40px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                />
              </PieChart>
            </div>
          </div>
        );
      case 'line':
        return (
          <div className="uw1_chart_scroll_container">
            <LineChart 
              data={getLineChartData(data)}
              margin={margin}
              width={chartWidth}
              height={300}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="progress" stroke="#3498db" />
            </LineChart>
          </div>
        );
      default:
        return null;
    }
  };

  // # Save data to Firestore
  const saveToFirestore = async (data) => {
    if (!targetUserId) return;

    try {
      const docRef = doc(db, 's1_mentee', targetUserId);

      // # Get current document data
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.data() || {};

      // # Update only the uworld field
      await setDoc(docRef, {
        ...currentData,
        uworld: data
      }, { merge: true });

    } catch (error) {
      console.error('Error saving qbank data:', error);
      // You might want to show an error message to the user here
    }
  };

  // # Get current data based on edit mode
  const currentData = editMode ? tempData : qbankData;
  const stats = calculateStats(currentData);
  const isLocked = !stepAccess;

  return (
    <div className={`uw1_container ${isDarkMode ? 'uw1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="uw1_main">
        <div className="uw1_content_wrapper">
          <h1 className="uw1_title">
            UWorld Question Bank
            {isMentor && <span className="uw1_mentor_pill">Mentor</span>}
          </h1>
          
          {/* Step 1 section */}
          <div className="uw1_step_section">
            <div className="uw1_step_header">
              <img 
                src={stepOneIcon} 
                alt="Step 1" 
                className="uw1_step_icon" 
              />
              <h2>UWorld - Step 1 First Pass</h2>
              {!isLocked && canEdit && (
                <button 
                  className="uw1_edit_button"
                  onClick={() => {
                    if (editMode) {
                      saveChanges();
                    } else {
                      toggleEditMode();
                    }
                  }}
                >
                  {editMode ? <Save /> : <Edit />}
                </button>
              )}
              {editMode && (
                <button 
                  className="uw1_cancel_button"
                  onClick={toggleEditMode}
                >
                  <Cancel />
                </button>
              )}
              {isLocked && (
                <div className="uw1_lock_icon">
                  <Lock />
                  <span className="uw1_lock_text">Subscription Required</span>
                </div>
              )}
            </div>

            {!isLocked && (
              <div className="uw1_step_content">
                {/* Table */}
                <div className="uw1_table_container">
                  <table className="uw1_table">
                    <thead>
                      <tr>
                        <th>Chapter</th>
                        <th>Status</th>
                        <th>Percentage</th>
                        <th>Correct</th>
                        <th>Incorrect</th>
                        <th>Weak Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SYSTEMS.map((system) => (
                        <tr key={system} className={system === "Random" ? "random_row" : ""}>
                          <td>
                            {system === "Random" ? (
                              <div className="uw1_system_with_icon">
                                <Shuffle className="uw1_system_icon" />
                                <span>{system}</span>
                                <div className="uw1_random_note">
                                  <FaExclamationCircle />
                                  <span>Excluded from stats</span>
                                </div>
                              </div>
                            ) : (
                              system
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <select
                                className={`uw1_status_select uw1_status_${(currentData[system]?.status || 'Not Started').toLowerCase().replace(/ /g, '_')}`}
                                value={currentData[system]?.status || 'Not Started'}
                                onChange={(e) => handleInputChange(system, 'status', e.target.value)}
                              >
                                {STATUS_OPTIONS.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`uw1_status_${(currentData[system]?.status || 'Not Started').toLowerCase().replace(/ /g, '_')}`}>
                                {currentData[system]?.status || 'Not Started'}
                              </span>
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={currentData[system]?.percentage === 0 ? '' : currentData[system]?.percentage || ''}
                                  onChange={(e) => handleInputChange(system, 'percentage', e.target.value)}
                                  className="uw1_edit_input"
                                />
                              </div>
                            ) : (
                              <span>
                                {currentData[system]?.percentage || 0}%
                              </span>
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                value={currentData[system]?.correct === 0 ? '' : currentData[system]?.correct || ''}
                                onChange={(e) => handleInputChange(system, 'correct', e.target.value)}
                                className="uw1_edit_input"
                              />
                            ) : (
                              currentData[system]?.correct || 0
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                value={currentData[system]?.incorrect === 0 ? '' : currentData[system]?.incorrect || ''}
                                onChange={(e) => handleInputChange(system, 'incorrect', e.target.value)}
                                className="uw1_edit_input"
                              />
                            ) : (
                              currentData[system]?.incorrect || 0
                            )}
                          </td>
                          <td>
                            <div className="uw1_weakness_cell">
                              {editMode ? (
                                <div className="uw1_weakness_edit_container">
                                  <div className="uw1_weakness_list">
                                    {currentData[system]?.weaknesses?.length > 0 && (
                                      <ul>
                                        {currentData[system]?.weaknesses.map((weakness, index) => (
                                          <li key={index}>
                                            {weakness}
                                            <button
                                              className="uw1_remove_weakness_btn"
                                              onClick={() => {
                                                const updatedWeaknesses = currentData[system]?.weaknesses.filter((_, i) => i !== index);
                                                handleInputChange(system, 'weaknesses', updatedWeaknesses);
                                              }}
                                            >
                                              ×
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  <div className="uw1_weakness_input_container">
                                    <input
                                      type="text"
                                      className="uw1_edit_input uw1_weakness_input"
                                      placeholder="Type new weakness"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                          const currentWeaknesses = currentData[system]?.weaknesses || [];
                                          handleInputChange(
                                            system,
                                            'weaknesses',
                                            [...currentWeaknesses, e.target.value.trim()]
                                          );
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                    <button 
                                      className="uw1_add_weakness_btn"
                                      onClick={(e) => {
                                        const input = e.target.parentElement.querySelector('input');
                                        if (input.value.trim()) {
                                          const currentWeaknesses = currentData[system]?.weaknesses || [];
                                          handleInputChange(
                                            system,
                                            'weaknesses',
                                            [...currentWeaknesses, input.value.trim()]
                                          );
                                          input.value = '';
                                        }
                                      }}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="uw1_weakness_list">
                                  {currentData[system]?.weaknesses?.length > 0 ? (
                                    <ul>
                                      {currentData[system]?.weaknesses.map((weakness, index) => (
                                        <li key={index}>{weakness}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span>None</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Dashboard */}
                <div className="uw1_dashboard">
                  <div className="uw1_dashboard_stats">
                    <div className="uw1_stat_card">
                      <h3>Progress</h3>
                      <p>{stats.totalCompleted} / {stats.totalChapters}</p>
                    </div>
                    <div className="uw1_stat_card">
                      <h3>Total Correct</h3>
                      <p>{stats.totalCorrect}</p>
                    </div>
                    <div className="uw1_stat_card">
                      <h3>Total Incorrect</h3>
                      <p>{stats.totalIncorrect}</p>
                    </div>
                    <div className="uw1_stat_card">
                      <h3>Total Percentage</h3>
                      <p>{stats.totalPercentage}%</p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="uw1_charts">
                    <div className="uw1_chart_controls">
                      <button 
                        className={`uw1_chart_type_btn ${selectedChart === 'bar' ? 'active' : ''}`}
                        onClick={() => setSelectedChart('bar')}
                      >
                        Bar Chart
                      </button>
                      <button 
                        className={`uw1_chart_type_btn ${selectedChart === 'pie' ? 'active' : ''}`}
                        onClick={() => setSelectedChart('pie')}
                      >
                        Pie Chart
                      </button>
                      <button 
                        className={`uw1_chart_type_btn ${selectedChart === 'line' ? 'active' : ''}`}
                        onClick={() => setSelectedChart('line')}
                      >
                        Line Chart
                      </button>
                    </div>
                    <div className="uw1_chart_container">
                      <h3>Performance by System</h3>
                      {renderChart(currentData)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="uw1_footer_wrapper">
        <div className="uw1_footer_content">
          <DashboardFooter isDarkMode={isDarkMode} />
        </div>
      </div>
    </div>
  );
}

export default UWorldQBank;
